import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ChevronLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Award,
  Camera,
  Save,
  Loader2
} from 'lucide-react';

const DoctorProfile = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialty: '',
    experience: '',
    education: '',
    bio: '',
    consultationFee: '',
    clinicAddress: '',
    languages: [],
  });

  const specialties = [
    { value: 'cardiology', label: lang === 'ar' ? 'أمراض القلب' : 'Cardiology' },
    { value: 'neurology', label: lang === 'ar' ? 'طب الأعصاب' : 'Neurology' },
    { value: 'pediatrics', label: lang === 'ar' ? 'طب الأطفال' : 'Pediatrics' },
    { value: 'dermatology', label: lang === 'ar' ? 'الأمراض الجلدية' : 'Dermatology' },
    { value: 'orthopedics', label: lang === 'ar' ? 'جراحة العظام' : 'Orthopedics' },
    { value: 'ophthalmology', label: lang === 'ar' ? 'طب العيون' : 'Ophthalmology' },
    { value: 'psychiatry', label: lang === 'ar' ? 'الطب النفسي' : 'Psychiatry' },
    { value: 'general', label: lang === 'ar' ? 'طب عام' : 'General Medicine' },
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Load mock profile data
    setProfile({
      firstName: user?.first_name || 'Ahmed',
      lastName: user?.last_name || 'Hassan',
      email: user?.email || 'dr.ahmed@dactrha.com',
      phone: user?.phone || '+963 999 123 456',
      specialty: 'cardiology',
      experience: '15',
      education: lang === 'ar' ? 'دكتوراه في الطب - جامعة دمشق' : 'MD - Damascus University',
      bio: lang === 'ar' 
        ? 'طبيب قلب متخصص مع خبرة أكثر من 15 عاماً في علاج أمراض القلب والأوعية الدموية.'
        : 'Specialized cardiologist with over 15 years of experience in treating cardiovascular diseases.',
      consultationFee: '50',
      clinicAddress: lang === 'ar' ? 'شارع المزة، دمشق' : 'Mazzeh Street, Damascus',
      languages: ['ar', 'en'],
    });
  }, [isAuthenticated, navigate, user, lang]);

  const handleSave = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success(lang === 'ar' ? 'تم حفظ الملف الشخصي' : 'Profile saved successfully');
    setLoading(false);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-profile-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'الملف الشخصي' : 'Profile Settings'}
                </h1>
                <p className="text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'إدارة معلوماتك الشخصية' : 'Manage your personal information'}
                </p>
              </div>
            </div>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSave} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {lang === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        {/* Profile Photo */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="relative">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-teal-600 text-white text-2xl">
                    {profile.firstName?.charAt(0)}{profile.lastName?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <Button size="icon" className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-teal-600 hover:bg-teal-700">
                  <Camera className="h-4 w-4" />
                </Button>
              </div>
              <div>
                <h3 className="text-lg font-semibold">Dr. {profile.firstName} {profile.lastName}</h3>
                <p className="text-slate-500 dark:text-slate-400">{specialties.find(s => s.value === profile.specialty)?.label}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              {lang === 'ar' ? 'المعلومات الشخصية' : 'Personal Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{lang === 'ar' ? 'الاسم الأول' : 'First Name'}</Label>
                <Input value={profile.firstName} onChange={(e) => setProfile({...profile, firstName: e.target.value})} />
              </div>
              <div>
                <Label>{lang === 'ar' ? 'الاسم الأخير' : 'Last Name'}</Label>
                <Input value={profile.lastName} onChange={(e) => setProfile({...profile, lastName: e.target.value})} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={profile.email} onChange={(e) => setProfile({...profile, email: e.target.value})} className="pl-10" />
                </div>
              </div>
              <div>
                <Label>{lang === 'ar' ? 'رقم الهاتف' : 'Phone'}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} className="pl-10" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Information */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              {lang === 'ar' ? 'المعلومات المهنية' : 'Professional Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{lang === 'ar' ? 'التخصص' : 'Specialty'}</Label>
                <Select value={profile.specialty} onValueChange={(v) => setProfile({...profile, specialty: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {specialties.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{lang === 'ar' ? 'سنوات الخبرة' : 'Years of Experience'}</Label>
                <Input type="number" value={profile.experience} onChange={(e) => setProfile({...profile, experience: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>{lang === 'ar' ? 'التعليم والشهادات' : 'Education & Certifications'}</Label>
              <div className="relative">
                <GraduationCap className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Textarea value={profile.education} onChange={(e) => setProfile({...profile, education: e.target.value})} className="pl-10" rows={2} />
              </div>
            </div>
            <div>
              <Label>{lang === 'ar' ? 'نبذة عنك' : 'Bio'}</Label>
              <Textarea value={profile.bio} onChange={(e) => setProfile({...profile, bio: e.target.value})} rows={3} placeholder={lang === 'ar' ? 'اكتب نبذة عن خبرتك وتخصصك...' : 'Write about your experience and expertise...'} />
            </div>
          </CardContent>
        </Card>

        {/* Practice Information */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {lang === 'ar' ? 'معلومات العيادة' : 'Practice Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{lang === 'ar' ? 'رسوم الاستشارة' : 'Consultation Fee'}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input type="number" value={profile.consultationFee} onChange={(e) => setProfile({...profile, consultationFee: e.target.value})} className="pl-8" />
                </div>
              </div>
              <div>
                <Label>{lang === 'ar' ? 'عنوان العيادة' : 'Clinic Address'}</Label>
                <Input value={profile.clinicAddress} onChange={(e) => setProfile({...profile, clinicAddress: e.target.value})} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorProfile;
